import {copyImageToClipboard} from "copy-image-clipboard";
import {Bindings} from "data/constants/bindings";
import {SocialNetwork, ShareType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ISharingStore} from "data/stores/sharing/sharing.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {getImageFromBackend, share} from "data/utils";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {SHARE_URL} from "data/constants";

interface IParams {
	imageUrl: string;
	contestId: number;
	shareType: ShareType;
	shareMessage: string;
	nativeTitle?: string;
}

export interface ISharingController extends ViewController<IParams> {
	shareFacebook: () => void;
	shareTwitter: () => void;
	shareNative: () => void;
	copyToClipboard: () => void;

	get i18n(): ILocalizationStore;

	get isImageCopied(): boolean;

	get imageToCopy(): string;
}

@injectable()
export class SharingController implements ISharingController {
	@observable _imageUrl: string = "";
	@observable _contestId: number = 0;
	@observable _shareType?: ShareType;
	@observable private _isImageCopied: boolean = false;
	@observable private _shareMessage: string = "";
	@observable private _nativeTitle: string = "";

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.SharingStore) private _sharingStore: ISharingStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	public get isImageCopied(): boolean {
		return this._isImageCopied;
	}

	public get imageToCopy(): string {
		return getImageFromBackend(this._sharingStore.shareImage?.fileName);
	}

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._imageUrl = param.imageUrl;
		this._shareType = param.shareType;
		this._contestId = param.contestId;
		this._shareMessage = param.shareMessage;
		this._nativeTitle = param.nativeTitle || "";
	}

	onChange(param: IParams): void {
		this.init(param);
	}

	get shareLink() {
		if (!this._shareType) {
			return;
		}
		const shareURL = `${SHARE_URL}${this.i18n.lang}/fan`;
		const uid = this._userStore.isAuthorized ? this._userStore.uid : this._userStore.guestUid;
		return {
			[ShareType.UserPick]: `${shareURL}/user-pick/${this._contestId}/${uid}`,
			[ShareType.Winner]: `${shareURL}/winner/${this._contestId}`,
		}[this._shareType];
	}

	public shareTwitter = () => {
		const link = this.shareLink;
		if (!link) {
			return;
		}
		share({
			socialNetwork: SocialNetwork.Twitter,
			message: this._shareMessage,
			link: `${link}?social=x`,
		});
	};

	public shareFacebook = () => {
		const link = this.shareLink;
		if (!link) {
			return;
		}
		share({
			socialNetwork: SocialNetwork.Facebook,
			link,
		});
	};

	public shareNative = async () => {
		const link = this.shareLink;
		if (!link) {
			return;
		}
		if (this.imageToCopy) {
			const blob = await fetch(this.imageToCopy).then((r) => r.blob());
			share({
				socialNetwork: SocialNetwork.Mobile,
				link,
				files: [
					new File([blob], "brackets_image.jpg", {
						type: blob.type,
					}),
				],
				title: this._nativeTitle,
				message: this._shareMessage,
			});

			return;
		}

		share({
			socialNetwork: SocialNetwork.Mobile,
			link,
			message: this._shareMessage,
		});
	};

	public copyToClipboard = () => {
		if (!this.imageToCopy) {
			return;
		}

		copyImageToClipboard(this.imageToCopy)
			.catch(() => {
				this._isImageCopied = false;
			})
			.finally(() => {
				setTimeout(() => {
					this._isImageCopied = false;
				}, 2000);
			});
	};
}
